import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ACCESS_STATUS } from "@/pages/UsersPage";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ChevronLeft, ChevronRight, Save, Send, Ban, Pencil } from "lucide-react";

const AREAS = [
  ["dashboard", "Dashboard", "Übersicht & Kennzahlen"],
  ["ausgaben", "Ausgaben / Board", "Artikel, Kategorien, Kommentare, Drive-Ordner"],
  ["partner", "Partner / Sponsoren", "Partnerprofile, Freianzeigen, Mail-Protokoll"],
  ["bezahlkunden", "Anzeigenkunden", "Kundenprofile, Anzeigen-Anfragen, Mail-Protokoll"],
  ["buchhaltung", "Buchhaltung", "Ansehen: Belege, Offene Posten, Jahresübersicht · Bearbeiten: Belege erneut senden (Einzelaktionen unten)"],
  ["mails", "Mailvorlagen", "Vorlagen & Versand-Protokoll"],
  ["mitwirkende", "Mitwirkende", "Personenliste & Profile"],
  ["einstellungen", "Einstellungen", "Anzeigenformate, Status-Labels"],
];
const LEVELS = [["none", "Nicht erlaubt", "bg-red-600"], ["view", "Ansehen", "bg-amber-500"], ["edit", "Bearbeiten", "bg-emerald-600"]];
const FLAGS = {
  ausgaben: [
    ["artikel_bearbeiten", "Artikel bearbeiten", "Artikel anlegen, ändern, verschieben (setzt „Ausgaben: Ansehen“ voraus) – Ausgaben: Bearbeiten gilt nur für Ausgaben & Kategorien"],
    ["artikel_status", "Artikel-Status ändern", "Status-Labels der Artikel umschalten (setzt „Ausgaben: Ansehen“ voraus); ohne dieses Recht nur ansehen"],
    ["artikel_loeschen", "Artikel löschen", "Darf Artikel im Board löschen (setzt „Artikel bearbeiten“ voraus)"],
    ["kategorien_verschieben", "Kategorien verschieben", "Darf Kategorien per Maus umsortieren (setzt „Ausgaben: Bearbeiten“ voraus)"],
    ["drive_zugriff", "Google-Drive-Zugriff", "Sieht Drive-Links von Artikeln, Druckunterlagen & Belegen (wer den Link hat, kann Inhalte ändern); Ordner erstellen zusätzlich mit „Artikel bearbeiten“"],
  ],
  buchhaltung: [
    ["bh_drucken", "Belege drucken / herunterladen", "PDFs, Sammeldruck, Auswertung & Jahresübersicht als PDF (setzt „Buchhaltung: Ansehen“ voraus)"],
    ["bh_rechnung", "Rechnungen erstellen & senden", "Rechnung aus „Noch nicht abgerechnet“ erstellen (setzt „Buchhaltung: Bearbeiten“ voraus)"],
    ["bh_zahlung", "Zahlung erfassen", "Als bezahlt markieren (Datum, Bank) / wieder offen (setzt „Buchhaltung: Bearbeiten“ voraus)"],
    ["bh_mahnung", "Mahnungen erstellen", "1.–3. Mahnung erstellen und senden (setzt „Buchhaltung: Bearbeiten“ voraus)"],
    ["bh_storno", "Rechnungen stornieren", "Stornorechnung erstellen (setzt „Buchhaltung: Bearbeiten“ voraus)"],
  ],
};
const FLAG_LEVELS = [["none", "Nicht erlaubt", "bg-red-600"], ["edit", "Erlaubt", "bg-emerald-600"]];

function PermRow({ k, label, hint, levels, perms, setPerms, locked, sub }) {
  return (
    <tr data-testid={`perm-row-${k}`}>
      <td className={`px-4 py-3 ${sub ? "pl-10" : ""}`}><div className="font-medium text-slate-800">{label}</div><div className="text-xs text-slate-400">{hint}</div></td>
      <td className="px-4 py-3">
        <div className="flex justify-end gap-1">
          {levels.map(([lv, l, on]) => (
            <button key={lv} type="button" disabled={locked} onClick={() => setPerms({ ...perms, [k]: lv })} data-testid={`perm-${k}-${lv}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${(perms[k] || "none") === lv ? `${on} text-white border-transparent` : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"} disabled:opacity-60`}>
              {l}
            </button>
          ))}
        </div>
      </td>
    </tr>
  );
}

function Matrix({ perms, setPerms, locked }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto" data-testid="permissions-matrix">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
          <tr><th className="text-left px-4 py-3">Bereich</th><th className="text-right px-4 py-3">Berechtigung</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {AREAS.map(([key, label, hint]) => [
            <PermRow key={key} k={key} label={label} hint={hint} levels={LEVELS} perms={perms} setPerms={setPerms} locked={locked} />,
            ...((FLAGS[key] || []).map(([fk, fl, fh]) => (
              <PermRow key={fk} k={fk} label={fl} hint={fh} levels={FLAG_LEVELS} perms={perms} setPerms={setPerms} locked={locked} sub />
            ))),
          ])}
        </tbody>
      </table>
    </div>
  );
}

export default function UserAccessPage() {
  const { personId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [entry, setEntry] = useState(null);
  const [perms, setPerms] = useState({});
  const [ids, setIds] = useState(location.state?.ids || []);
  const [busy, setBusy] = useState(false);
  const { user: me } = useAuth();

  const apply = (d) => { setEntry(d); setPerms(d.permissions); };
  const load = useCallback(() => {
    api.get(`/access/${personId}`).then((r) => apply(r.data)).catch((e) => { toast.error(apiError(e.response?.data?.detail)); navigate("/benutzer"); });
  }, [personId, navigate]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!location.state?.ids) { api.get("/access").then((r) => setIds(r.data.map((x) => x.person_id))).catch(() => {}); }
  }, [location.state]);

  const dirty = entry && JSON.stringify(entry.permissions) !== JSON.stringify(perms);
  const act = async (fn, msg) => {
    setBusy(true);
    try { const { data } = await fn(); apply(data); toast.success(msg); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); load(); }
    finally { setBusy(false); }
  };
  const save = () => act(() => api.put(`/access/${personId}/permissions`, { permissions: perms }), "Rechte gespeichert");
  const invite = async () => {
    if (dirty) { await api.put(`/access/${personId}/permissions`, { permissions: perms }).catch(() => {}); }
    act(() => api.post(`/access/${personId}/activate`), "Zugang freigeschaltet (keine Mail versendet)");
  };
  const sendInvite = () => act(() => api.post(`/access/${personId}/send-invite`), "Einladungsmail versendet");
  const revoke = () => { if (window.confirm("Zugang wirklich sperren?")) { act(() => api.post(`/access/${personId}/revoke`), "Zugang gesperrt"); } };
  const go = (id) => { if (dirty && !window.confirm("Ungespeicherte Änderungen verwerfen?")) return; navigate(`/benutzer/${id}`, { state: { ids } }); };

  if (!entry) return null;
  const idx = ids.indexOf(personId);
  const chef = entry.status === "chef";
  const hasLogin = ["aktiv", "eingeladen", "freigeschaltet"].includes(entry.status);

  return (
    <div className="p-6 lg:p-8 max-w-4xl">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <Link to="/benutzer" className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="user-back"><ArrowLeft size={15} /> Benutzer</Link>
        {idx >= 0 && (
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={idx === 0} onClick={() => go(ids[idx - 1])} data-testid="user-prev"><ChevronLeft size={16} /> Vorherige</Button>
            <span className="text-sm text-slate-500 px-2" data-testid="user-pager-pos">{idx + 1} / {ids.length}</span>
            <Button variant="outline" size="sm" disabled={idx === ids.length - 1} onClick={() => go(ids[idx + 1])} data-testid="user-next">Nächste <ChevronRight size={16} /></Button>
          </div>
        )}
      </div>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="user-detail-title">{entry.name}</h1>
          <div className="mt-1 text-sm text-slate-500">{entry.email || "keine E-Mail"}</div>
          <span className={`inline-block mt-2 text-xs font-medium px-2 py-1 rounded border ${ACCESS_STATUS[entry.status].cls}`} data-testid="user-detail-status">{ACCESS_STATUS[entry.status].label}</span>
          {entry.last_login && <span className="ml-2 text-xs text-slate-400">Letzte Anmeldung: {new Date(entry.last_login).toLocaleString("de-DE")}</span>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" asChild data-testid="user-edit-person"><Link to={`/einstellungen/mitwirkende/${personId}`}><Pencil size={15} className="mr-1.5" /> Kontaktdaten</Link></Button>
          {!chef && !hasLogin && <Button onClick={invite} disabled={busy || !entry.email} className="bg-blue-600 hover:bg-blue-700" data-testid="user-invite"><Send size={15} className="mr-1.5" /> Zugang freischalten</Button>}
          {!chef && hasLogin && <Button variant="outline" onClick={sendInvite} disabled={busy || !me?.is_chef} title={me?.is_chef ? "Einladung per Systemmail senden" : "Nur für den Chefredakteur"} data-testid="user-send-invite"><Send size={15} className="mr-1.5" /> {entry.invited_at ? "Einladung erneut senden" : "Einladungsmail senden"}</Button>}
          {!chef && hasLogin && <Button variant="outline" onClick={revoke} disabled={busy} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="user-revoke"><Ban size={15} className="mr-1.5" /> Zugang sperren</Button>}
          {!chef && <Button onClick={save} disabled={busy || !dirty} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="user-save-permissions"><Save size={15} className="mr-1.5" /> Rechte speichern{dirty ? " *" : ""}</Button>}
        </div>
      </div>
      {chef && <p className="mb-4 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">Der Chefredakteur darf immer alles – die Rechte sind fest und nicht änderbar. Anmeldung per Passwort.</p>}
      {!chef && <p className="mb-4 text-sm text-slate-500">Anmeldung erfolgt ohne Passwort: Die Person gibt ihre E-Mail ein und erhält einen Code (10 Minuten gültig). Mailversand bleibt dem Chefredakteur vorbehalten.</p>}
      <Matrix perms={perms} setPerms={setPerms} locked={chef} />
    </div>
  );
}
